import React, { useEffect, useState } from 'react'
import { sortBy } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import Select from 'react-select'
import { setPublicSubjects } from '../../../redux/actions/subjects'
import { getPublicOrganisations } from '../../../redux/actions/organisations'
import { setPublicEducationTypes } from '../../../redux/actions/education-types'
import { CHALLENGE_SPACE_SORT_OPTIONS } from '../../../utils/constants'
import Control from '../../shared/filtering/SortDirectionControl'

interface FilterParams {
	include: string
	page: {
		offset: number
		limit: number
	}
	filter: {
		is_public: number
		state: number
		subject_id?: string
		organisation_id?: string
		education_type_id?: string
	}
	sort: string
}

const subjectParams = {
	page: {
		offset: 0,
		limit: 999,
	},
	sort: 'title',
}

const orgParams = {
	page: {
		offset: 0,
		limit: 999,
	},
	sort: 'created_at',
	filter: {
		state: 1,
	},
}

const educationTypeParams = {
	page: {
		offset: 0,
		limit: 999,
	},
	sort: '-created_at',
}

const ChallengeSpaceFilters = ({
	currentValues,
	onChange,
	hideOrganizers = false,
}) => {
	const [filterParams, setFilterParams] = useState<FilterParams>({
		include: 'subject',
		page: {
			offset: 0,
			limit: 9,
		},
		filter: {
			is_public: 1,
			state: 1,
		},
		// sort: 'challenge_submit_deadline'
		sort: 'challenge_submit_deadline_expired,challenge_submit_deadline',
	})

	const subjects = useSelector((state) => state['public'].subject)
	const organisations = useSelector((state) => state['public'].organisation)
	const educationTypes = useSelector((state) => state['public'].educationType)

	let subjectList = []

	if (Object.keys(subjects).length) {
		subjectList = Object.keys(subjects).map((s) => subjects[s])
		subjectList = sortBy(subjectList, (s) => s.attributes.title)
	}

	let orgList = []

	if (Object.keys(organisations).length) {
		orgList = Object.keys(organisations).map((s) => organisations[s])
		orgList = sortBy(orgList, (s) => s.attributes.name)
	}

	let educationTypeList = []

	if (Object.keys(educationTypes).length) {
		educationTypeList = Object.keys(educationTypes).map(
			(s) => educationTypes[s]
		)
	}

	const dispatch = useDispatch()

	useEffect(() => {
		setFilterParams(currentValues)
	}, [currentValues])

	useEffect(() => {
		dispatch(setPublicSubjects(subjectParams))
		dispatch(getPublicOrganisations(orgParams))
		dispatch(setPublicEducationTypes(educationTypeParams))
	}, [])

	const onSort = (sort: any) => {
		// Toggle minus(-) sign for ASC/DESC
		let newParams = {
			...filterParams,
			page: {
				...filterParams.page,
				offset: 0,
			},
			sort: sort.value,
		}
		setFilterParams(newParams)
		onChange(newParams)
	}

	const selectSubject = (subject: any) => {
		let newParams = {
			...filterParams,
			page: {
				...filterParams.page,
				offset: 0,
			},
			filter: {
				...filterParams.filter,
				subject_id: subject ? subject?.id : null,
			},
		}
		if (!subject) {
			delete newParams.filter.subject_id
		}
		setFilterParams(newParams)
		onChange(newParams)
	}

	const selectOrganisation = (org: any) => {
		let newParams = {
			...filterParams,
			page: {
				...filterParams.page,
				offset: 0,
			},
			filter: {
				...filterParams.filter,
				organisation_id: org ? org.id : null,
			},
		}
		if (!org) {
			delete newParams.filter.organisation_id
		}
		setFilterParams(newParams)
		onChange(newParams)
	}

	const selectEducationType = (t: any) => {
		let newParams = {
			...filterParams,
			page: {
				...filterParams.page,
				offset: 0,
			},
			filter: {
				...filterParams.filter,
				education_type_id: t ? t.id : null,
			},
		}
		if (!t) {
			delete newParams.filter.education_type_id
		}
		setFilterParams(newParams)
		onChange(newParams)
	}

	return (
		<div className="container px-4 mx-auto">
			<div className="flex flex-wrap items-center justify-between py-6 w-full relative z-30 bg-white">
				{subjectList.length && (
					<div className="mr-4 mb-4 flex-grow">
						<label>Topic</label>
						<Select
							name="subjects"
							instanceId="subjectSelect"
							getOptionLabel={(option) => option.attributes.title}
							getOptionValue={(option) => option}
							value={
								filterParams.filter && filterParams.filter.subject_id
									? subjectList.filter((subject) => {
											return subject?.id === filterParams.filter.subject_id
									  })
									: null
							}
							placeholder="All topics"
							options={subjectList}
							onChange={selectSubject}
							isClearable={true}
							isSearchable={false}
							className="react-select-container z-50"
							classNamePrefix="react-select"
						/>
					</div>
				)}

				{!hideOrganizers && (
					<div className="mr-4 mb-4 flex-grow">
						<label>Organizer</label>
						<Select
							name="orgs"
							instanceId="orgSelect"
							getOptionLabel={(option) => option.attributes.name}
							getOptionValue={(option) => option}
							value={
								filterParams.filter && filterParams.filter.organisation_id
									? orgList.filter((org) => {
											return org.id === filterParams.filter.organisation_id
									  })
									: null
							}
							placeholder="All organizers"
							options={orgList}
							onChange={selectOrganisation}
							isClearable={true}
							isSearchable={false}
							className="react-select-container z-40"
							classNamePrefix="react-select"
						/>
					</div>
				)}
				<div className="mb-4 mr-4 flex-grow">
					<label>Level</label>
					<Select
						name="educationType"
						instanceId="educationTypeSelect"
						getOptionLabel={(option) => option.attributes.name}
						getOptionValue={(option) => option}
						value={
							filterParams.filter && filterParams.filter.education_type_id
								? educationTypeList.filter((t) => {
										return t.id === filterParams.filter.education_type_id
								  })
								: null
						}
						placeholder="All levels"
						options={educationTypeList}
						onChange={selectEducationType}
						isClearable={true}
						isSearchable={false}
						className="react-select-container z-30"
						classNamePrefix="react-select"
					/>
				</div>
				<div className="mb-4">
					<label>Sort by</label>
					<Select
						name="sortBy"
						instanceId="sortBy"
						defaultValue={CHALLENGE_SPACE_SORT_OPTIONS.find((a) =>
							a.value.includes('challenge_submit_deadline')
						)}
						value={CHALLENGE_SPACE_SORT_OPTIONS.find((option) => {
							return option.value === filterParams.sort
						})}
						placeholder="Sort:"
						options={CHALLENGE_SPACE_SORT_OPTIONS}
						onChange={onSort}
						components={{ Control }}
						isClearable={false}
						isSearchable={false}
						className="react-select-container z-20"
						classNamePrefix="react-select"
					/>
				</div>
			</div>
		</div>
	)
}

export default ChallengeSpaceFilters
